import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  useInboundRoutes, useSaveInboundRoute, useDeleteInboundRoute, useSchedules, useBusinessHours,
  useRoutingMode, useSetRoutingMode,
} from '../../lib/api/hooks-tms.js';
import { ApiClientError } from '../../lib/api/client.js';
import { toastError } from '../../components/ui/Toast.js';
import { FlowEditor } from './flows/FlowEditor.js';
import type { RoutingMode } from '@arcnet/contracts';
import { useSession } from '../../lib/auth/session.js';
import { TableSkeleton, Empty, ErrorBanner, Badge, Segmented, CardHead, SectionHead } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { RowAction } from '../../components/ui/RowAction.js';
import { confirmAction } from '../../components/ui/Confirm.js';
import { Switch } from '../../components/ui/Form.js';
import { DestinationPicker, DestinationLabel } from '../../components/telephony/DestinationPicker.js';
import {
  HoursEditor, blankHours, hoursFromRules, rulesFromHours, invalidDay, type HoursDraft,
} from '../../components/telephony/HoursEditor.js';
import { RouteFlow } from './RouteFlow.js';
import { RouteHistory } from './RouteHistory.js';
import type { Destination, InboundRoute } from '@arcnet/contracts';
import type { PanelMode } from './flows/panel.js';

/**
 * Inbound routes (docs/26 D1, D2).
 *
 * A route is named, numbers are assigned to it, and it says where their calls
 * go -- with an hours split on a schedule the account already has, or on hours
 * of its own. The list of routes sits beside the open one, and below the form
 * is what the route actually does, followed to the end of every branch.
 *
 * It was one route per number, edited on each number's row: the same decision
 * made again for every number that shared it.
 *
 * Laid out as three steps -- numbers, hours, details -- with where calls go open
 * and closed side by side under the hours that choose between them, and what the
 * route does summarised beneath while it is edited (owner chose this layout,
 * "Option B", 20 September 2026). Numbers are chips with a search, because a
 * grid of fifty checkboxes is not a way to find one number.
 */

type HoursMode = InboundRoute['hours']['mode'];

/**
 * The time condition, in the order people think of it: none, the account's
 * business hours, or something custom. It was "Always the same / On a schedule /
 * Hours of its own", which read as three unrelated things (owner, 14 September
 * 2026). Only the wording and order moved: a route is still saved as hours mode
 * 'always', a 'schedule' (business hours being the account's nominated one), or
 * hours of its 'own', so no saved route changes.
 */
type HoursChoice = 'always' | 'business' | 'custom';
const CHOICES: Array<[HoursChoice, string]> = [
  ['always', 'Always open'], ['business', 'Business hours'], ['custom', 'Custom'],
];
const CUSTOM_KINDS: Array<['own' | 'schedule', string]> = [
  ['own', 'Hours for this route'], ['schedule', 'Another schedule'],
];

export interface NumberChoice {
  id: string; e164: string; label: string | null; routeId: string | null; routeName: string | null;
}

/** "0105025383", "10 502" and "Durban" all find +27105025383 "Durban". */
function matches(n: NumberChoice, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  if (n.label?.toLowerCase().includes(q)) return true;
  const digits = q.replace(/\D/g, '');
  if (!digits) return false;
  const e = n.e164.replace(/\D/g, '');
  // A national number: the leading 0 stands for the country code.
  return e.includes(digits) || (digits.startsWith('0') && e.includes(digits.slice(1)));
}

export function InboundRoutesTab({ tenantId }: { tenantId: string }) {
  const can = useSession((s) => s.can);
  const writable = can('inboundroute:write');
  const routes = useInboundRoutes(tenantId);
  const [selected, setSelected] = useState<string | null>(null);
  const [seedNumber, setSeedNumber] = useState<string | null>(null);

  const list = useMemo(() => routes.data?.data ?? [], [routes.data]);
  const unrouted = useMemo(() => routes.data?.unrouted ?? [], [routes.data]);
  const current = selected && selected !== 'new' ? list.find((r) => r.id === selected) ?? null : null;

  // The first route opens by itself: an empty right-hand side is a wasted screen.
  useEffect(() => {
    if (selected === null && list[0]) setSelected(list[0].id);
  }, [list, selected]);

  /** Every number on the account, and the route it is on now. */
  const numbers: NumberChoice[] = useMemo(() => [
    ...list.flatMap((r) => r.numbers.map((n) => ({
      id: n.id, e164: n.e164, label: n.label ?? null, routeId: r.id, routeName: r.name,
    }))),
    ...unrouted.map((n) => ({ id: n.id, e164: n.e164, label: n.label ?? null, routeId: null, routeName: null })),
  ].sort((a, b) => a.e164.localeCompare(b.e164)), [list, unrouted]);

  const startNew = (numberId: string | null) => { setSeedNumber(numberId); setSelected('new'); };

  /**
   * Classic or Flows (docs/41; owner, 6 October 2026: "a client can do it"). The two edit the same
   * routing, so switching changes which editor opens and never what a call does.
   */
  const mode = useRoutingMode(tenantId);
  const setMode = useSetRoutingMode(tenantId);
  const flows = mode.data?.mode === 'flows';
  const [newFlow, setNewFlow] = useState(0);
  const switchMode = (m: RoutingMode) => setMode.mutate(m, {
    onError: (e) => {
      if (e instanceof ApiClientError && e.code === 'routing_mode.drafts_open') {
        void confirmAction({ title: 'Switch to Classic?', body: <>Calls won’t change. {e.message}</>, action: 'Stay in Flows' });
      } else toastError(e);
    },
  });

  const head = (
    <SectionHead title="Inbound routes"
      sub={flows
        ? 'Each route’s numbers, and what happens to their calls, from the first ring.'
        : 'Named routes, the numbers on each, and where their calls go.'}
      action={<>
        <span className="arc-muted" style={{ fontSize: 12.5 }}>Edit routing as</span>
        {can('flow:publish') && mode.data ? (
          <Segmented label="Edit routing as" value={mode.data.mode}
            options={[['classic', 'Classic'], ['flows', 'Flows']]} onChange={switchMode} />
        ) : <strong style={{ fontSize: 12.5 }}>{flows ? 'Flows' : 'Classic'}</strong>}
        {flows ? can('flow:write') && (
          <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" onClick={() => setNewFlow((n) => n + 1)}>
            <Icon name="plus" size={13} /> New call flow
          </button>
        ) : writable && (
          <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" onClick={() => startNew(null)}>
            <Icon name="plus" size={13} /> New route
          </button>
        )}
      </>} />
  );

  if (mode.data === undefined && !mode.error) {
    return <>{head}<div className="arc-card arc-card-body"><div className="arc-skeleton" style={{ height: 240 }} /></div></>;
  }
  if (flows) {
    return (
      <>
        {head}
        {unrouted.length > 0 && (
          <div className="arc-banner arc-banner-warn" style={{ marginBottom: 14 }}>
            <Icon name="warning" size={14} />
            <span>
              <span className="arc-mono">{unrouted.map((n) => n.e164).join(', ')}</span>{' '}
              {unrouted.length === 1 ? 'is on no route, so calls to it are' : 'are on no route, so calls to them are'} refused.
              Add {unrouted.length === 1 ? 'it' : 'them'} to a flow from its “Call comes in” step.
            </span>
          </div>
        )}
        <FlowEditor tenantId={tenantId} writable={can('flow:write')} canPublish={can('flow:publish')} newFlowRequest={newFlow} />
      </>
    );
  }

  return (
    <>
      {head}

      {routes.error && <ErrorBanner error={routes.error} />}

      {/* The one fault worth a banner: calls to these numbers are refused. */}
      {unrouted.length > 0 && (
        <div className="arc-banner arc-banner-warn" style={{ marginBottom: 14 }}>
          <Icon name="warning" size={14} />
          <span>
            <span className="arc-mono">{unrouted.map((n) => n.e164).join(', ')}</span>{' '}
            {unrouted.length === 1 ? 'is on no route, so calls to it are' : 'are on no route, so calls to them are'} refused.
          </span>
          {writable && (
            <button type="button" className="arc-btn arc-btn-sm" style={{ marginLeft: 'auto' }}
              onClick={() => startNew(unrouted[0]!.id)}>
              Route {unrouted.length === 1 ? 'it' : unrouted[0]!.e164}
            </button>
          )}
        </div>
      )}

      <div className="arc-routes">
        <nav className="arc-card arc-routes-list" aria-label="Inbound routes">
          {routes.data === undefined ? <TableSkeleton rows={4} cols={1} /> : list.length === 0 ? (
            <Empty icon="ivr" title="No routes yet" hint="A route sends calls to your numbers somewhere." />
          ) : list.map((r) => (
            <button key={r.id} type="button" className="arc-route-item"
              aria-current={selected === r.id} onClick={() => { setSelected(r.id); setSeedNumber(null); }}>
              <span className="arc-row" style={{ gap: 6 }}>
                <strong className="arc-truncate">{r.name}</strong>
                <span className="arc-spacer" />
                {!r.is_enabled && <Badge tone="warn">off</Badge>}
                {r.is_enabled && r.hours.open_now !== null && (
                  <Badge tone={r.hours.open_now ? 'ok' : 'muted'}>{r.hours.open_now ? 'open' : 'closed'}</Badge>
                )}
              </span>
              <span className="arc-subtle" style={{ fontSize: 11.5 }}>
                {r.numbers.length === 0 ? 'no numbers' : r.numbers.length === 1 ? '1 number' : `${r.numbers.length} numbers`}
              </span>
              <span className="arc-muted arc-truncate" style={{ fontSize: 12 }}>
                <DestinationLabel tenantId={tenantId} destination={r.destination} />
              </span>
            </button>
          ))}
        </nav>

        <div style={{ minWidth: 0 }}>
          {selected === 'new' || current ? (
            <RouteEditor
              key={current?.id ?? `new-${seedNumber ?? ''}`}
              tenantId={tenantId} existing={current} numbers={numbers}
              seedNumberId={seedNumber} writable={writable}
              history={{ canName: can('flow:write'), canPublish: can('flow:publish') }}
              onSaved={(id) => { setSelected(id); setSeedNumber(null); }}
              onDeleted={() => setSelected(null)}
              onCancel={() => { setSelected(list[0]?.id ?? null); setSeedNumber(null); }}
            />
          ) : !routes.isLoading && (
            <div className="arc-card">
              <Empty icon="ivr" title="Choose a route" hint="Its numbers, where calls go and what happens open here." />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/** One step of the route: its number, a heading, and the controls. */
function Step({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <section className="arc-route-step">
      <span className="arc-route-step-n" aria-hidden>{n}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <h3 className="arc-route-step-title">{title}</h3>
        {children}
      </div>
    </section>
  );
}

/**
 * The route's numbers and the rest, as two lists side by side (owner, 5 October 2026: option C,
 * "we need to be able to search numbers on both assigned and unassigned numbers"). Each list has
 * its own search. A number moves by dragging it across, by selecting it -- or several -- and using
 * the arrows, or by double-clicking it; the arrows are also how it is done without a mouse
 * (WCAG 2.5.7). Numbers on no route come first on the right: they are the ones refusing calls.
 *
 * A number is on one route at a time, so one taken from another route moves here when the route
 * is saved -- and says so until then ("moves from Route to Priya"), rather than quietly.
 */
function NumberPanes({ numbers, value, onChange, routeId, disabled }: {
  numbers: NumberChoice[]; value: string[]; onChange: (ids: string[]) => void; routeId: string | null; disabled: boolean;
}) {
  const [qOn, setQOn] = useState('');
  const [qOff, setQOff] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [over, setOver] = useState<'on' | 'off' | null>(null);

  const on = value.map((id) => numbers.find((n) => n.id === id)).filter((n): n is NumberChoice => !!n);
  const off = numbers.filter((n) => !value.includes(n.id))
    .sort((x, y) => Number(x.routeId !== null) - Number(y.routeId !== null) || x.e164.localeCompare(y.e164));
  const onShown = on.filter((n) => matches(n, qOn));
  const offShown = off.filter((n) => matches(n, qOff));
  const pickedOn = on.filter((n) => picked.has(n.id)).map((n) => n.id);
  const pickedOff = off.filter((n) => picked.has(n.id)).map((n) => n.id);

  const add = (ids: string[]): void => {
    onChange([...value, ...ids.filter((id) => !value.includes(id))]);
    setPicked((p) => new Set([...p].filter((id) => !ids.includes(id))));
  };
  const remove = (ids: string[]): void => {
    onChange(value.filter((id) => !ids.includes(id)));
    setPicked((p) => new Set([...p].filter((id) => !ids.includes(id))));
  };
  const toggle = (id: string): void => setPicked((p) => {
    const next = new Set(p);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  if (numbers.length === 0) {
    return <p className="arc-subtle" style={{ margin: 0 }}>No numbers are assigned to this account yet.</p>;
  }

  // What a drag carries: the selection on that side if the row is in it, otherwise the row alone.
  const dragIds = (id: string, side: 'on' | 'off'): string[] => {
    const sel = side === 'on' ? pickedOn : pickedOff;
    return sel.includes(id) ? sel : [id];
  };
  const pane = (side: 'on' | 'off') => ({
    onDragOver: (e: React.DragEvent) => {
      if (disabled || !e.dataTransfer.types.includes('application/x-arc-numbers')) return;
      e.preventDefault();
      setOver(side);
    },
    onDragLeave: (e: React.DragEvent) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(null);
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      setOver(null);
      const raw = e.dataTransfer.getData('application/x-arc-numbers');
      if (!raw) return;
      const { from, ids } = JSON.parse(raw) as { from: 'on' | 'off'; ids: string[] };
      if (from === side) return;
      if (side === 'on') add(ids); else remove(ids);
    },
  });
  const row = (n: NumberChoice, side: 'on' | 'off') => {
    const moving = side === 'on' && n.routeId !== null && n.routeId !== routeId;
    return (
      <li key={n.id}>
        <button type="button" className="arc-numpane-row" aria-pressed={picked.has(n.id)} disabled={disabled}
          draggable={!disabled}
          onDragStart={(e) => {
            e.dataTransfer.setData('application/x-arc-numbers', JSON.stringify({ from: side, ids: dragIds(n.id, side) }));
            e.dataTransfer.effectAllowed = 'move';
          }}
          onClick={() => toggle(n.id)}
          onDoubleClick={() => (side === 'on' ? remove([n.id]) : add([n.id]))}>
          <Icon name="grip" size={13} />
          <span className="arc-mono">{n.e164}</span>
          <span className="arc-truncate">{n.label ?? ''}</span>
          {side === 'on'
            ? moving && <span className="arc-numpane-tag" data-tone="info">moves from {n.routeName}</span>
            : <span className="arc-numpane-tag" data-tone={n.routeId ? undefined : 'warn'}>
                {n.routeName ? `on ${n.routeName}` : 'no route'}
              </span>}
        </button>
      </li>
    );
  };

  return (
    <>
      <div className="arc-numpanes">
        <section className="arc-numpane" data-over={over === 'on' || undefined} aria-label="Numbers on this route" {...pane('on')}>
          <div className="arc-numpane-head">
            <strong>On this route</strong><span className="arc-subtle arc-num">{on.length}</span>
            <input className="arc-input" value={qOn} onChange={(e) => setQOn(e.target.value)}
              placeholder="Search" aria-label="Search numbers on this route" />
          </div>
          {onShown.length > 0 ? <ul className="arc-numpane-list">{onShown.map((n) => row(n, 'on'))}</ul>
            : <p className="arc-numpane-empty">{on.length === 0 ? 'Drag numbers here, or pick them on the right.' : 'No number here matches.'}</p>}
        </section>

        <div className="arc-numpane-mid">
          <button type="button" className="arc-btn arc-btn-sm arc-btn-icon" disabled={disabled || pickedOff.length === 0}
            aria-label="Add the selected numbers to this route" title="Add selected" onClick={() => add(pickedOff)}>
            <Icon name="chevronLeft" size={14} />
          </button>
          <button type="button" className="arc-btn arc-btn-sm arc-btn-icon" disabled={disabled || pickedOn.length === 0}
            aria-label="Take the selected numbers off this route" title="Remove selected" onClick={() => remove(pickedOn)}>
            <Icon name="chevronRight" size={14} />
          </button>
        </div>

        <section className="arc-numpane" data-over={over === 'off' || undefined} aria-label="Other numbers" {...pane('off')}>
          <div className="arc-numpane-head">
            <strong>Other numbers</strong><span className="arc-subtle arc-num">{off.length}</span>
            <input className="arc-input" value={qOff} onChange={(e) => setQOff(e.target.value)}
              placeholder="Search" aria-label="Search other numbers" />
          </div>
          {offShown.length > 0 ? <ul className="arc-numpane-list">{offShown.map((n) => row(n, 'off'))}</ul>
            : <p className="arc-numpane-empty">{off.length === 0 ? 'Every number is on this route.' : 'No number matches.'}</p>}
        </section>
      </div>
      <p className="arc-hint">
        Drag numbers across, or select them and use the arrows. A number is on one route at a time: one
        taken from another route moves here when you save.
      </p>
    </>
  );
}

/** A destination as the path summary shows it. */
function PathBox({ tenantId, destination, lit }: {
  tenantId: string; destination: Destination | null; lit?: boolean;
}) {
  return (
    <span className={`arc-route-path-box${lit ? ' arc-route-path-box-on' : ''}`}>
      <DestinationLabel tenantId={tenantId} destination={destination} />
    </span>
  );
}

export function RouteEditor({ tenantId, existing, numbers, seedNumberId, writable, history, onSaved, onDeleted, onCancel, panel }: {
  tenantId: string;
  existing: InboundRoute | null;
  numbers: NumberChoice[];
  seedNumberId: string | null;
  writable: boolean;
  /** Classic: who may name versions and restore one (docs/41 §12). Absent in a flow's side panel. */
  history?: { canName: boolean; canPublish: boolean } | undefined;
  onSaved: (id: string) => void;
  onDeleted: () => void;
  onCancel: () => void;
  /** In a call flow's side panel: the "Call comes in" step, applied into the draft instead of saved. */
  panel?: PanelMode;
}) {
  const save = useSaveInboundRoute(tenantId);
  const remove = useDeleteInboundRoute(tenantId);
  const schedules = useSchedules(tenantId);
  const business = useBusinessHours(tenantId);

  const [name, setName] = useState(existing?.name ?? '');
  const [numberIds, setNumberIds] = useState<string[]>(
    () => existing ? existing.numbers.map((n) => n.id) : seedNumberId ? [seedNumberId] : []);
  const [destination, setDestination] = useState<Destination | null>(existing?.destination ?? null);
  const [mode, setMode] = useState<HoursMode>(existing?.hours.mode ?? 'always');
  const [scheduleId, setScheduleId] = useState(
    existing?.hours.mode === 'schedule' ? existing.hours.schedule_id ?? '' : '');
  const [hours, setHours] = useState<HoursDraft>(
    () => existing?.hours.mode === 'own' ? hoursFromRules(existing.hours.rules) : blankHours());
  const [timezone, setTimezone] = useState(existing?.hours.mode === 'own' ? existing.hours.timezone : '');
  const [closed, setClosed] = useState<Destination | null>(existing?.closed_destination ?? null);
  const [prefix, setPrefix] = useState(existing?.cid_name_prefix ?? '');
  const [enabled, setEnabled] = useState(existing?.is_enabled ?? true);
  // Open or closed now is only known for the hours as saved.
  const [hoursEdited, setHoursEdited] = useState(false);
  /**
   * The time condition as picked. Null until somebody picks one and derived from
   * the saved hours until then, because which schedule is business hours arrives
   * in a request of its own.
   */
  const [picked, setPicked] = useState<HoursChoice | null>(null);

  const shared = schedules.data?.data ?? [];
  const chosen = shared.find((s) => s.id === scheduleId);
  const businessId = business.data?.schedule_id ?? null;
  const others = shared.filter((s) => s.id !== businessId);
  const choice: HoursChoice = picked
    ?? (mode === 'always' ? 'always'
      : mode === 'schedule' && scheduleId !== '' && scheduleId === businessId ? 'business'
        : 'custom');

  const pickHours = (c: HoursChoice) => {
    setPicked(c);
    setHoursEdited(true);
    if (c === 'always') {
      setMode('always');
    } else if (c === 'business') {
      setMode('schedule');
      setScheduleId(businessId ?? '');
    } else if (mode === 'always' || choice === 'business') {
      // Custom starts at hours for this route; another schedule is one click on.
      setMode('own');
    }
  };

  const missing: string[] = [];
  if (!name.trim()) missing.push('a name');
  if (!destination) missing.push('where callers go');
  if (choice === 'business' && !businessId) missing.push('business hours for the account');
  if (choice === 'custom' && mode === 'schedule' && !scheduleId) missing.push('a schedule');
  if (mode !== 'always' && !closed) missing.push('where callers go outside the hours');
  if (mode === 'own' && invalidDay(hours)) missing.push('hours that close after they open');

  const savedOpenNow = !hoursEdited && existing && existing.hours.mode === mode
    && (mode !== 'schedule' || existing.hours.schedule_id === scheduleId)
    ? existing.hours.open_now : null;

  const body = () => ({
    name: name.trim(),
    number_ids: numberIds,
    destination,
    hours: mode === 'always' ? { mode }
      // Business hours as they are when saved, even if they loaded after the pick.
      : mode === 'schedule' ? { mode, schedule_id: choice === 'business' ? businessId ?? scheduleId : scheduleId }
        : { mode, timezone: timezone.trim() || null, rules: rulesFromHours(hours) },
    closed_destination: mode === 'always' ? null : closed,
    cid_name_prefix: prefix.trim() || null,
    is_enabled: enabled,
  });
  const submit = () => save.mutate({ ...(existing ? { id: existing.id } : {}), ...body() },
    { onSuccess: (res) => onSaved(existing?.id ?? (res as { id: string }).id) });

  const onRoute = numbers.filter((n) => numberIds.includes(n.id));
  const hoursLabel = mode === 'always' ? null : choice === 'business'
              ? `Business hours${business.data?.name ? ` (${business.data.name})` : ''}`
              : mode === 'own'
                ? 'Custom hours'
                : chosen ? `Schedule: ${chosen.name}` : 'A schedule, not chosen yet';

  return (
    <form id={panel?.formId} onSubmit={(e) => { e.preventDefault(); if (panel) panel.onApply(body()); }}>
      <div className="arc-card">
        <div className="arc-card-head">
          <h2 className="arc-h2 arc-truncate">{existing ? existing.name : 'New route'}</h2>
          <div className="arc-spacer" />
          {existing && history && !panel && (
            <RouteHistory tenantId={tenantId} routeId={existing.id} routeName={existing.name}
              canName={history.canName} canPublish={history.canPublish} />
          )}
          <div className="arc-row" style={{ gap: 8 }}>
            <label htmlFor="ir-on" className="arc-muted" style={{ cursor: 'pointer', fontSize: 12.5 }}>
              {enabled ? 'Taking calls' : 'Switched off: calls are refused'}
            </label>
            <Switch id="ir-on" checked={enabled} disabled={!writable} onChange={setEnabled} />
          </div>
        </div>

        <fieldset className="arc-card-body" disabled={!writable}
          style={{ border: 0, margin: 0, minWidth: 0 }}>
          <Step n={1} title="Numbers on this route">
            <NumberPanes numbers={numbers} value={numberIds} onChange={setNumberIds} disabled={!writable}
              routeId={existing?.id ?? null} />
          </Step>

          <Step n={2} title="Hours">
            <Segmented label="Hours" value={choice} options={CHOICES} onChange={pickHours} />

            {choice === 'custom' && (
              <div style={{ marginTop: 12 }}>
                <Segmented label="Custom hours" value={mode === 'schedule' ? 'schedule' : 'own'}
                  options={CUSTOM_KINDS}
                  onChange={(k) => {
                    setHoursEdited(true);
                    setMode(k);
                    if (k === 'schedule') setScheduleId((id) => (id === businessId ? '' : id));
                  }} />

                {mode === 'schedule' && (
                  <div className="arc-field" style={{ marginTop: 12, maxWidth: 380 }}>
                    <label className="arc-label" htmlFor="ir-schedule">Schedule</label>
                    <select id="ir-schedule" className="arc-select" value={scheduleId}
                      onChange={(e) => { setScheduleId(e.target.value); setHoursEdited(true); }}>
                      <option value="">Choose a schedule…</option>
                      {others.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                    {others.length === 0 && (
                      <p className="arc-hint">No other schedules yet. Add one under Hours &amp; schedules, or set hours for this route.</p>
                    )}
                  </div>
                )}

                {mode === 'own' && (
                  <div style={{ marginTop: 12 }}>
                    <div className="arc-field" style={{ maxWidth: 260 }}>
                      <label className="arc-label" htmlFor="ir-tz">Timezone</label>
                      <input id="ir-tz" className="arc-input" value={timezone} placeholder="The account's"
                        onChange={(e) => { setTimezone(e.target.value); setHoursEdited(true); }} />
                    </div>
                    <HoursEditor idPrefix="ir-hours" value={hours}
                      onChange={(h) => { setHours(h); setHoursEdited(true); }} />
                  </div>
                )}
              </div>
            )}

            {/* Where calls go, open and closed together, under the hours that choose between them. */}
            <div className="arc-route-dests">
              <span className="arc-route-dest-when" style={mode === 'always' ? undefined : { color: 'var(--arc-ok)' }}>
                {mode === 'always' ? 'Calls go to' : 'Open'}
              </span>
              <DestinationPicker tenantId={tenantId} value={destination} onChange={setDestination} />
              {mode !== 'always' && (
                <>
                  <span className="arc-route-dest-when arc-muted">Closed</span>
                  <DestinationPicker tenantId={tenantId} value={closed} onChange={setClosed} />
                </>
              )}
            </div>

            {choice === 'business' && (
              <p className="arc-hint">
                {businessId
                  ? `The account's business hours${business.data?.name ? ` (${business.data.name})` : ''}, set under Hours & schedules.`
                  : 'This account has no business hours yet. Set them under Hours & schedules, or choose Custom.'}
                {savedOpenNow !== null && businessId ? (savedOpenNow ? ' Open now.' : ' Closed now.') : ''}
              </p>
            )}
          </Step>

          <Step n={3} title="Details">
            <div className="arc-row" style={{ gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="arc-field" style={{ margin: 0, flex: '1 1 220px', maxWidth: 320 }}>
                <label className="arc-label" htmlFor="ir-name">Route name</label>
                <input id="ir-name" className="arc-input" maxLength={80} value={name}
                  onChange={(e) => setName(e.target.value)} placeholder="Main line" />
              </div>
              <div className="arc-field" style={{ margin: 0, flex: '1 1 220px', maxWidth: 320 }}>
                <label className="arc-label" htmlFor="ir-prefix">Prepend to caller ID</label>
                <input id="ir-prefix" className="arc-input" maxLength={40} value={prefix}
                  onChange={(e) => setPrefix(e.target.value)} placeholder="Sales"
                  title="Shown before the caller's name on the phones that ring, so whoever answers knows which number was called." />
              </div>
            </div>
          </Step>
        </fieldset>
      </div>

      {!panel && <div className="arc-card" style={{ marginTop: 14 }}>
        <CardHead title="What happens" action={
          <RouteFlow tenantId={tenantId} title={name.trim() || undefined} route={{
            numbers: onRoute.map((n) => n.e164),
            destination,
            hours: hoursLabel === null ? null : { label: hoursLabel, openNow: savedOpenNow ?? null },
            closed: mode === 'always' ? null : closed,
            enabled,
          }} />
        } />
        <div className="arc-card-body">
          {!enabled ? (
            <p className="arc-muted" style={{ margin: 0 }}>Switched off: calls to these numbers are refused.</p>
          ) : (
            <>
              <div className="arc-row" style={{ gap: 8, flexWrap: 'wrap' }}>
                <span className="arc-route-path-box arc-mono">
                  {onRoute.length === 0 ? <span className="arc-subtle">no numbers</span>
                    : onRoute.length <= 2 ? onRoute.map((n) => n.e164).join(', ')
                      : `${onRoute.length} numbers`}
                </span>
                <Icon name="chevronRight" size={13} />
                {hoursLabel === null ? (
                  <PathBox tenantId={tenantId} destination={destination} lit />
                ) : (
                  <span className="arc-route-path-box">{hoursLabel}</span>
                )}
              </div>
              {hoursLabel !== null && (
                <div className="arc-route-path-branches">
                  <div className="arc-row" style={{ gap: 8 }}>
                    <span className="arc-route-dest-when" style={{ color: 'var(--arc-ok)' }}>Open</span>
                    <PathBox tenantId={tenantId} destination={destination} lit={savedOpenNow === true} />
                  </div>
                  <div className="arc-row" style={{ gap: 8 }}>
                    <span className="arc-route-dest-when arc-muted">Closed</span>
                    <PathBox tenantId={tenantId} destination={closed} lit={savedOpenNow === false} />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>}

      {writable && !panel && (
        <div className="arc-row" style={{ gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
          <button type="button" className="arc-btn arc-btn-primary"
            disabled={save.isPending || missing.length > 0} onClick={submit}>
            {save.isPending ? 'Saving…' : existing ? 'Save route' : 'Create route'}
          </button>
          <button type="button" className="arc-btn" onClick={onCancel}>Cancel</button>
          {missing.length > 0 && (
            <span className="arc-subtle" style={{ fontSize: 12 }}>Needs {missing.join(', ')}.</span>
          )}
          <div className="arc-spacer" />
          {existing && (
            <RowAction kind="delete" label={`Delete ${existing.name}`} disabled={remove.isPending} onClick={() => {
              void confirmAction({
                title: `Delete the route "${existing.name}"?`,
                body: existing.numbers.length > 0
                  ? `${existing.numbers.map((n) => n.e164).join(', ')} will be on no route.`
                  : 'No numbers use it.',
                action: 'Delete route', danger: true,
              }).then((ok) => { if (ok) remove.mutate(existing.id, { onSuccess: onDeleted }); });
            }} />
          )}
        </div>
      )}
    </form>
  );
}
